import {
  boxShape,
  canReveal,
  digitAt,
  digitCount,
  geometry,
  hintAvailable,
  hintsLeft,
  isGiven,
  isWrong,
  shownDigit,
  SUDOKU_MAX_MISTAKES,
  type SudokuBoard as Board,
  type SudokuState,
} from "@bing/engine-sudoku";
import type { ReactNode } from "react";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";

/**
 * THE GRID — `dir="ltr"` ALWAYS: the app is RTL and a CSS grid inherits it, which would paint cell 0
 * on the RIGHT (CLAUDE.md, «A CSS GRID INHERITS dir="rtl"»). One button per cell, so a tap is a real
 * hit on the painted cell, never arithmetic on a pointer position.
 *
 * Highlights: the selected cell's row, column and box; every cell showing the selected cell's digit;
 * the selected cell itself. RED, by the game's mode (the operator, 2026-11-04): with mistake checking
 * ("check") every wrong digit, at once; without ("free") only the cells «نشانم بده» marked, each
 * until its number changes. Every other number the player puts in has ONE colour, right or wrong —
 * nothing green anywhere. Pure paint: no timer, no animation.
 *
 * ⚠️ ONE CELL LOOKS CHOSEN, WHATEVER MIX OF MOUSE, TOUCH AND KEYS (the review's I1): Chromium (Telegram
 * Desktop on Windows/Linux) focuses a clicked button and rings it once a key is pressed, so a focus
 * left behind on the clicked cell looked like a second selection — and Space/Enter re-clicked it. The
 * WAI-ARIA grid pattern: FOCUS IS THE SELECTION. Only the selected cell is in the tab order (cell 0
 * while none is), a cell that gets focus (a click, Tab) becomes the selected one, and the screen moves
 * focus with every key it handles.
 */
export function SudokuGrid({
  board,
  state,
  selected,
  side,
  zoom = 1,
  onSelect,
}: {
  board: Board;
  state: SudokuState;
  selected: number | null;
  /** The board's side in CSS px (measured by the screen). */
  side: number;
  /** The page's text zoom (measured by the screen; 1 = none). */
  zoom?: number;
  onSelect: (cell: number) => void;
}): ReactNode {
  const { size } = board;
  const g = geometry(size);
  const box = boxShape(size);
  const cell = side / size;
  // THE NOTES' SIZE: a share of the cell — but a text zoom scales every font-size while the cell
  // keeps its box, so a full cell's rows (3 on the 9×9, 2 on the 6×6) are capped to what is left of
  // the cell (inside the board's 2 px frame) past its lines (≤ 2 px), its padding (2 px) and 1 px of
  // air, once zoomed. Never below 7 px as drawn.
  const noteRows = size / box.cols;
  const noteRoom = (side - 4) / size - 5;
  const notePx = Math.max(
    7 / zoom,
    Math.min(cell * (size === 9 ? 0.24 : 0.2), noteRoom / (noteRows * zoom)),
  );
  const selDigit = selected === null ? 0 : shownDigit(board, state, selected);
  const selUnits = selected === null ? null : (g.unitsOf[selected] ?? null);
  return (
    <div
      dir="ltr"
      role="grid"
      aria-label={fa.sudoku.boardLabel}
      data-testid="sudoku-board"
      className="sudoku-board"
      style={{
        width: side,
        height: side,
        gridTemplateColumns: `repeat(${size}, 1fr)`,
        gridTemplateRows: `repeat(${size}, 1fr)`,
      }}
    >
      {Array.from({ length: g.cells }, (_, i) => {
        const r = Math.floor(i / size);
        const c = i % size;
        const units = g.unitsOf[i];
        const digit = shownDigit(board, state, i);
        const given = isGiven(board, i);
        const hinted = state.hinted.includes(i);
        const wrong = state.mode === "check" ? isWrong(board, state, i) : state.marked.includes(i);
        const related =
          selUnits !== null &&
          units !== undefined &&
          (units[0] === selUnits[0] || units[1] === selUnits[1] || units[2] === selUnits[2]);
        const same = selDigit !== 0 && digit === selDigit;
        const classes = ["sudoku-cell"];
        // The lines: none on the outer edge (the board's frame is there), thick between boxes.
        const boxLeft = c > 0 && c % box.cols === 0;
        const boxTop = r > 0 && r % box.rows === 0;
        if (i === selected) classes.push("sudoku-cell--selected");
        else if (same) classes.push("sudoku-cell--same");
        else if (related) classes.push("sudoku-cell--related");
        if (given) classes.push("sudoku-cell--given");
        else if (hinted) classes.push("sudoku-cell--hinted");
        else if (wrong) classes.push("sudoku-cell--wrong");
        else if (digit !== 0) classes.push("sudoku-cell--entry");
        const notes = state.notes[i] ?? 0;
        return (
          <button
            key={i}
            type="button"
            role="gridcell"
            data-cell={i}
            aria-selected={i === selected}
            aria-label={`${faDigits(r + 1)}، ${faDigits(c + 1)}${digit ? `: ${faDigits(digit)}` : ""}`}
            tabIndex={i === (selected ?? 0) ? 0 : -1}
            className={classes.join(" ")}
            style={{
              fontSize: Math.round(cell * 0.56),
              borderLeftWidth: c === 0 ? 0 : boxLeft ? 2 : 1,
              borderTopWidth: r === 0 ? 0 : boxTop ? 2 : 1,
              borderLeftColor: boxLeft ? "var(--sudoku-line-box)" : "var(--sudoku-line)",
              borderTopColor: boxTop ? "var(--sudoku-line-box)" : "var(--sudoku-line)",
            }}
            onClick={() => onSelect(i)}
            onFocus={() => onSelect(i)}
          >
            {digit !== 0 ? (
              faDigits(digit)
            ) : notes !== 0 ? (
              <span
                className="sudoku-notes"
                style={{
                  gridTemplateColumns: `repeat(${box.cols}, 1fr)`,
                  fontSize: Math.floor(notePx * 10) / 10,
                }}
                aria-hidden
              >
                {Array.from({ length: size }, (_, k) => (
                  <span key={k}>{notes & (1 << k) ? faDigits(k + 1) : ""}</span>
                ))}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

type Tool = "notes" | "erase" | "undo" | "hint" | "reveal";

/**
 * THE PAD — the size's digits (one row on the 6×6; two on the 9×9, so every key stays thumb-wide even
 * at 307 px) and the four tools: notes (a toggle), erase, undo, hint (with how many are left). A digit
 * whose every copy is on the board is dimmed, not disabled — it can still go in.
 * With a GIVEN selected (a cell filled from the start) erase and notes are disabled: that cell can
 * never change (the operator, 2026-11-03).
 *
 * THE TOOLS ROW'S FIFTH PLACE — in the row, so nothing on the screen moves or shrinks: with mistake
 * checking, the mistake counter «اشتباه n/۳» (a display, not a key); without, «نشانم بده» from the
 * first full grid with mistakes until it is used (once per game).
 */
export function SudokuPad({
  board,
  state,
  notesMode,
  canUndo,
  givenSelected,
  onDigit,
  onTool,
}: {
  board: Board;
  state: SudokuState;
  notesMode: boolean;
  canUndo: boolean;
  /** The selected cell is a given: erase and notes are unavailable. */
  givenSelected: boolean;
  onDigit: (d: number) => void;
  onTool: (tool: Tool) => void;
}): ReactNode {
  const { size } = board;
  const left = hintsLeft(state);
  const rows: number[][] =
    size === 9
      ? [
          [1, 2, 3, 4, 5],
          [6, 7, 8, 9],
        ]
      : [[1, 2, 3, 4, 5, 6]];
  const tools: {
    tool: Tool;
    label: string;
    icon: ReactNode;
    on?: boolean;
    off?: boolean;
  }[] = [
    { tool: "notes", label: fa.sudoku.notes, icon: "✎", on: notesMode, off: givenSelected },
    { tool: "erase", label: fa.sudoku.erase, icon: "⌫", off: givenSelected },
    { tool: "undo", label: fa.sudoku.undo, icon: "↶", off: !canUndo },
    // "free": a hint fills empty cells only — none empty, none to give.
    { tool: "hint", label: fa.sudoku.hint, icon: "💡", off: !hintAvailable(board, state) },
  ];
  // Stays — and stays ON — until used, whatever the board holds: an off key would tell that nothing is
  // wrong, and the count is never shown again after the message (the operator, 2026-11-04).
  if (canReveal(state)) tools.push({ tool: "reveal", label: fa.sudoku.showWhere, icon: "🔍" });
  return (
    <div className="sudoku-pad" data-testid="sudoku-pad">
      <div className="sudoku-pad__tools">
        {tools.map(({ tool, label, icon, on, off }) => (
          <button
            key={tool}
            type="button"
            aria-label={label}
            aria-pressed={tool === "notes" ? Boolean(on) : undefined}
            disabled={off}
            data-testid={`sudoku-${tool}`}
            className={`sudoku-key sudoku-key--tool${on ? " sudoku-key--on" : ""}${
              tool === "reveal" ? " sudoku-key--reveal" : ""
            }`}
            onClick={() => onTool(tool)}
          >
            <span className="sudoku-key__icon" aria-hidden>
              {icon}
            </span>
            <span className="sudoku-key__label">{label}</span>
            {/* On the KEY's corner, not the icon's: a text zoom grows the icon and the label, and a
                badge hung off the icon was pushed above the key. */}
            {tool === "hint" ? (
              <span className="sudoku-key__badge" aria-hidden>
                {faDigits(left)}
              </span>
            ) : null}
          </button>
        ))}
        {state.mode === "check" ? (
          <div
            role="status"
            aria-label={fa.sudoku.mistakesLabel(state.mistakes, SUDOKU_MAX_MISTAKES)}
            data-testid="sudoku-mistakes"
            className={`sudoku-key sudoku-key--tool sudoku-key--count${
              state.mistakes > 0 ? " sudoku-key--count-on" : ""
            }`}
          >
            <span className="sudoku-key__icon tabular-nums" aria-hidden>
              {faDigits(state.mistakes)}/{faDigits(SUDOKU_MAX_MISTAKES)}
            </span>
            <span className="sudoku-key__label" aria-hidden>
              {fa.sudoku.mistakes}
            </span>
          </div>
        ) : null}
      </div>
      {rows.map((row, k) => (
        <div
          key={k}
          className="sudoku-pad__digits"
          dir="ltr"
          style={{ gridTemplateColumns: `repeat(${size === 9 ? 5 : 6}, 1fr)` }}
        >
          {row.map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`sudoku-digit-${d}`}
              aria-label={faDigits(d)}
              className={`sudoku-key sudoku-key--digit${
                digitCount(board, state, d) >= size ? " sudoku-key--done" : ""
              }${notesMode ? " sudoku-key--note" : ""}`}
              onClick={() => onDigit(d)}
            >
              {faDigits(d)}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Whether the selected cell takes a digit or a note at all (a given or a hinted cell does not). */
export function editable(board: Board, state: SudokuState, cell: number | null): boolean {
  return cell !== null && digitAt(board.puzzle, cell) === 0 && !state.hinted.includes(cell);
}
